import { BusinessAnalytics } from "@xepp/business-analytics-sdk";
import { maskEmail, maskPhoneNumber } from "../../pages/customer/onboarding/forgot-password/util";

/**
 * Customer sign-up events for the web app, mirroring the "Onboarding" category of the
 * shared analytics event sheet.
 *
 * The flow and its steps are seeded by the backend migration SeedProductAnalyticsCatalog
 * (flow 2) and must stay in step with it — ingestion accepts unknown codes over HTTP and
 * then silently discards them — so the codes live here and nowhere else.
 */
export const ONBOARDING_FLOW = "onboarding";

export const ONBOARDING_EVENTS = {
	signupStarted: "signup_started",
	signupCompleted: "signup_completed",
	phoneOtpSent: "phone_otp_sent",
	phoneOtpResent: "phone_otp_resent",
	phoneVerified: "phone_verified",
	emailOtpSent: "email_otp_sent",
	emailOtpResent: "email_otp_resent",
	emailVerified: "email_verified",
	passwordSet: "password_set",
} as const;

export type OnboardingStatus = "success" | "failed";

const SIGNUP_SOURCE = "Web app";

/** The web app has a single sign-up method: a password, with email and phone verified by OTP. */
const SIGNUP_METHOD = "password";

export const trackSignupStarted = (): void => {
	BusinessAnalytics.track(ONBOARDING_EVENTS.signupStarted, ONBOARDING_FLOW, { source: SIGNUP_SOURCE });
};

export const trackSignupCompleted = (durationS: number): void => {
	BusinessAnalytics.track(ONBOARDING_EVENTS.signupCompleted, ONBOARDING_FLOW, { method: SIGNUP_METHOD, duration_s: durationS });
};

/** `phone` is the raw number; only its masked form is sent. */
export const trackPhoneOtpSent = (phone: string): void => {
	BusinessAnalytics.track(ONBOARDING_EVENTS.phoneOtpSent, ONBOARDING_FLOW, { phone_number_masked: maskPhoneNumber(phone) });
};

export const trackPhoneOtpResent = (phone: string, resendCount: number): void => {
	BusinessAnalytics.track(ONBOARDING_EVENTS.phoneOtpResent, ONBOARDING_FLOW, { phone_number_masked: maskPhoneNumber(phone), resend_count: resendCount });
};

export const trackPhoneVerified = (status: OnboardingStatus, attempts: number): void => {
	BusinessAnalytics.track(ONBOARDING_EVENTS.phoneVerified, ONBOARDING_FLOW, { status, attempts });
};

/** `email` is the raw address; only its masked form is sent. */
export const trackEmailOtpSent = (email: string): void => {
	BusinessAnalytics.track(ONBOARDING_EVENTS.emailOtpSent, ONBOARDING_FLOW, { email_masked: maskEmail(email) });
};

export const trackEmailOtpResent = (email: string, resendCount: number): void => {
	BusinessAnalytics.track(ONBOARDING_EVENTS.emailOtpResent, ONBOARDING_FLOW, { email_masked: maskEmail(email), resend_count: resendCount });
};

export const trackEmailVerified = (status: OnboardingStatus, attempts: number): void => {
	BusinessAnalytics.track(ONBOARDING_EVENTS.emailVerified, ONBOARDING_FLOW, { status, attempts });
};

export const trackPasswordSet = (status: OnboardingStatus): void => {
	BusinessAnalytics.track(ONBOARDING_EVENTS.passwordSet, ONBOARDING_FLOW, { status });
};
